import EventEmitter from 'events';
import { filterTrackConstraints } from './lib';
import LoggerWrapper from '../../modules/logger-wrapper.js';
import { resolutions } from '@app/constants.js';

const logger = new LoggerWrapper('MEDIA_CORE', 'LocalMediaStream', LoggerWrapper.DEBUG);
logger.setRemoteVerbosity(LoggerWrapper.DEBUG);
class LocalMediaStream extends EventEmitter {
	constructor(mediaStream = null) {
		super(arguments);
		this.initialize(mediaStream);
	}

	initialize(mediaStream = null) {
		logger.debug('Creating LocalMediaStream', { mediaStream });
		if (mediaStream) {
			this.id = mediaStream.id;
			this.hasAudio = false;
			this.hasVideo = false;
			this.audioTrack = null;
			this.videoTrack = null;
			this._originalMediaStream = mediaStream;
			this._detect();
			this._addListeners();
		} else {
			throw new Error({ name: 'MediaStreamNull', message: "MediaStream can't be null." });
		}
	}

	_addListeners() {
		if (this.hasVideo) {
			this.videoTrack.onended = (e) => this._handleTrackLost('video', 'ended', e);
			this.videoTrack.onunmute = (e) => this._handleTrackMute('video', false);
			this.videoTrack.onmute = (e) => this._handleTrackMute('video', true);
		}

		if (this.hasAudio) {
			this.audioTrack.onended = (e) => this._handleTrackLost('audio', 'ended', e);
			this.audioTrack.onunmute = (e) => this._handleTrackMute('audio', false);
			this.audioTrack.onmute = (e) => this._handleTrackMute('audio', true);
		}
	}

	_handleTrackMute(type, muted) {
		logger.info(`Mute state of ${type} track changed to ${muted ? 'muted' : 'umuted'}`);

		if (type === 'video') {
			this.isVideoMuted = muted;
		} else {
			this.isAudioMuted = muted;
		}

		this.emit('track:mute:changed', type, muted);
	}

	/**
	 * Internal method to simulate losing video track.
	 */
	_simulateVideoLost() {
		this.videoTrack.stop();
		this._handleTrackLost('video', 'simulated');
	}

	_handleTrackLost(type, reason, e) {
		logger.info(`[${type}] track linked to [${this[`${type}Track`]?.label}] device lost. [${reason}]`, {
			track: this[`${type}Track`],
		});

		let lostTrack;

		if (type === 'video') {
			lostTrack = this.videoTrack;
			this._terminateVideoTrack();
		} else if (type === 'audio') {
			lostTrack = this.audioTrack;
			this._terminateAudioTrack();
		}

		this.emit('track:lost', type, reason, lostTrack?.getSettings(), e);
	}

	_terminateVideoTrack() {
		this.videoTrack.stop();
		this._originalMediaStream.removeTrack(this.videoTrack);
		this.videoTrack = null;
		this.hasVideo = false;
		this.isVideoMuted = false;
	}

	_terminateAudioTrack() {
		this.audioTrack.stop();
		this._originalMediaStream.removeTrack(this.audioTrack);
		this.audioTrack = null;
		this.hasAudio = false;
		this.isAudioMuted = false;
	}

	_detect() {
		this._detectAudio();
		this._detectVideo();
	}

	_detectAudio() {
		const tracks = this._originalMediaStream.getAudioTracks();
		this.audioTrack = tracks.length > 0 ? tracks[0] : null;
		this.hasAudio = !!this.audioTrack && this.audioTrack?.readyState !== 'ended';
		this.isAudioMuted = this.hasAudio && this.audioTrack?.muted;
		logger.debug(`Audio track [${this.hasAudio ? 'detected' : 'not detected'}] and [muted:${this.isAudioMuted}]`, {
			settings: this.audioTrack ? this.audioTrack.getSettings() : null,
		});
	}

	_detectVideo() {
		const tracks = this._originalMediaStream.getVideoTracks();
		this.videoTrack = tracks.length > 0 ? tracks[0] : null;
		this.hasVideo = !!this.videoTrack && this.videoTrack?.readyState !== 'ended';
		this.isVideoMuted = this.hasVideo && this.videoTrack?.muted;
		logger.debug(`Video track [${this.hasVideo ? 'detected' : 'not detected'}] and [muted:${this.isVideoMuted}]`, {
			settings: this.videoTrack ? this.videoTrack.getSettings() : null,
		});
	}

	async _handleResolutionAutoAdapter(currentConstraints, increase = false) {
		const newConstraints = this._getConstraintsLowerResolution(currentConstraints, increase);
		logger.debug('Trying to auto adapt resolution', {
			videoConstraints: currentConstraints.height,
			nextVideoConstraints: newConstraints.height,
		});

		await this.videoTrack.applyConstraints({
			height: newConstraints.height,
			width: newConstraints.width,
		});
		this.emit('track:adapter:resolution');
	}

	_getConstraintsLowerResolution(constraints, increase = false) {
		const lowerHeight = increase
			? constraints.height === 720
				? 1080
				: 2160
			: constraints.height === 2160
			? 1080
			: 720;
		return {
			height: lowerHeight,
			width: resolutions[lowerHeight],
		};
	}

	getAudioDeviceId() {
		let active = null;
		if (this.hasAudio) {
			active = this.audioTrack.getSettings().deviceId;
		}
		return active;
	}

	getVideoDeviceId() {
		let active = null;
		if (this.hasVideo) {
			active = this.videoTrack.getSettings().deviceId;
		}
		return active;
	}

	getAudioDeviceDetails() {
		let active = null;
		if (this.hasAudio) {
			active = this.audioTrack.getSettings();
		}
		return active;
	}

	getVideoDeviceDetails() {
		let active = null;
		if (this.hasVideo) {
			active = this.videoTrack.getSettings();
		}
		return active;
	}

	stop() {
		logger.debug('Stopping mediaStream...');
		const tracks = this._originalMediaStream.getTracks();

		tracks.forEach(function (track) {
			track.stop();
			track.onended = null;
			track.onmute = null;
			track.onunmute = null;
		});

		this._originalMediaStream = null;
		this.audioTrack = null;
		this.videoTrack = null;
	}

	get mediaStream() {
		return this._originalMediaStream;
	}
}

export default LocalMediaStream;
